import {
  type FC,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Card from 'components/Card';
import { RouteTypes } from 'RouteTypes';
import { sortByFields } from 'helpers/sortHelpers';
import { chunkArray } from 'util/chunkArray';
import ContentPopUp from '../ContentPopUp';
import {
  ColumnExactLink, ColumnLink,
} from '../Table';
import { numberWithCommas } from '../../helpers/TextHelpers';
import Select from '../Select';
import { useOffDesktopBodyScroll } from '../../hooks/useOffDesktopBodyScroll';
import { getToFixedFractionNumber } from '../../helpers/numberHelpers';
import type { CardsBundlePopupProps } from './types';
import * as S from './styled';

const selectOptions = [
  'Serial (low to high)',
  'Serial (high to low)',
  'Most recent',
  'Price (low to high)',
  'Price (high to low)',
  'Score (high to low)',
];

const listingSorts: string[] = [
  'edition asc price asc',
  'edition desc price asc',
  'purchase_time desc',
  'price asc edition_id asc',
  'price desc edition_id asc',
  'collector_score desc price asc',
];
const CardsBundlePopup: FC<CardsBundlePopupProps> = ({
  cards,
  collectionId,
  forSaleAmount,
  stakedAmount,
  creatorUsername,
  ownerUsername,
  onClose,
}) => {
  const { t } = useTranslation();
  useOffDesktopBodyScroll(false);
  const [curIndex, setCurIndex] = useState<number>(0);
  const cardLink = RouteTypes.Card
    .replace(':username', creatorUsername)
    .replace(':collectionId', collectionId)
    .replace(':cardId', cards[0].cardId || '');

  let sortedCards = cards;
  if (curIndex) {
    const sortBy = listingSorts[curIndex];
    sortedCards = sortByFields(cards, chunkArray(sortBy.split(' '), 2) as [string, 'asc' | 'desc'][]);
  }

  return (
    <ContentPopUp onClose={onClose} isMobileFixed>
      <S.HeadContainer>
        <S.CardWrapper>
          <Card
            card={cards[0]}
            cardUrl=""
            collectionId={collectionId}
            isCardPage
          />
        </S.CardWrapper>
        <S.GeneralInfo>
          <S.PurchaseHeadline font="secondary" bold uppercase>
            Owner:
            <br />
            <div>
              @
              {ownerUsername}
            </div>
          </S.PurchaseHeadline>
          <S.InfoPanel>
            <S.PurchaseLabel>
              Total cards
              <S.PurchaseValue>
                {cards.length}
              </S.PurchaseValue>
            </S.PurchaseLabel>
            <S.PurchaseLabel>
              For sale
              <S.PurchaseValue>
                {forSaleAmount}
              </S.PurchaseValue>
            </S.PurchaseLabel>
            <S.PurchaseLabel>
              Staked
              <S.PurchaseValue>
                {stakedAmount}
              </S.PurchaseValue>
            </S.PurchaseLabel>
            <S.PopUpSelectContainer>
              <Select
                values={selectOptions}
                defaultValueIndex={curIndex}
                label="sort by"
                onSelect={(val, i) => setCurIndex(i)}
                popupVersion
              />
            </S.PopUpSelectContainer>
          </S.InfoPanel>
        </S.GeneralInfo>
        <S.MobilePopUpSelectContainer>
          <Select
            values={selectOptions}
            defaultValueIndex={curIndex}
            label="sort by"
            onSelect={(val, i) => setCurIndex(i)}
            popupVersion
          />
        </S.MobilePopUpSelectContainer>
      </S.HeadContainer>
      <S.TableContainer>
        {
          cards.length > 0 && (
            <S.PurchaseTable>
              <S.PurchaseRow header>
                <S.Column>{t('card.purchase.serial')}</S.Column>
                <S.Column>Status</S.Column>
                <S.Column>{t('card.purchase.price')}</S.Column>
                <S.Column>{t('card.purchase.score')}</S.Column>
                <S.Column />
              </S.PurchaseRow>
              {
                sortedCards.map(({
                  edition, price, forSale, staked, collector_score,
                }) => {
                  const editionLink = cardLink.replace(':edition?', String(edition));
                  return (
                    <S.PurchaseRow
                      key={creatorUsername && edition + creatorUsername}
                    >
                      <S.Column bold>
                        <ColumnExactLink href={editionLink} target="_blank" rel="nofollow">
                          #
                          {edition}
                        </ColumnExactLink>
                      </S.Column>
                      <S.Column $isGrey={!price}>
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {staked ? 'Staked' : forSale ? 'For sale' : 'Not for sale'}
                      </S.Column>
                      <S.Column bold={!!price} $isGrey={!price}>
                        {price ? `$${numberWithCommas(price)}` : 'N/A'}
                      </S.Column>
                      <S.Column>
                        {numberWithCommas(collector_score || 0, getToFixedFractionNumber(collector_score || 0))}
                      </S.Column>
                      <S.BuyBtnColumn>
                        <ColumnLink to={editionLink}>
                          <S.BuyBtn
                            size="small"
                            background="dark"
                          >
                            {t('common.open')}
                          </S.BuyBtn>
                        </ColumnLink>
                      </S.BuyBtnColumn>
                    </S.PurchaseRow>
                  );
                })
              }
            </S.PurchaseTable>
          )
        }
      </S.TableContainer>
    </ContentPopUp>
  );
};

export default CardsBundlePopup;
